import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import Navbar from './Navbar';
import { supabase } from './supabaseClient';
import { createCheckoutSession } from './stripe';
import { hasProAccess, getSubscriptionDisplay, getMonthlyExportStatus } from './utils/subscription';
import SEO from './components/SEO';

export default function DashboardPage() {
  const { user, loading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [savedGUIs, setSavedGUIs] = useState([]);
  const [loadingGUIs, setLoadingGUIs] = useState(false);
  const [dashboardMessage, setDashboardMessage] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [forceRender, setForceRender] = useState(false);
  const [exportStatus, setExportStatus] = useState(null);

  // Force render after 5 seconds to prevent infinite loading
  useEffect(() => {
    const timer = setTimeout(() => {
      setForceRender(true);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Manual refresh function to reload user data
  const manualRefreshUser = async () => {
    if (!user) return;
    
    try {
      // Use the AuthContext's refreshUser function to properly update user state
      const success = await refreshUser();
      
      if (success) {
        setDashboardMessage('Profile refreshed successfully.');
        setTimeout(() => setDashboardMessage(''), 3000);
      } else {
        setDashboardMessage('Failed to refresh profile.');
        setTimeout(() => setDashboardMessage(''), 3000);
      }
      
    } catch (error) {
      console.error('Error in manual refresh:', error);
      setDashboardMessage('Error refreshing profile.');
      setTimeout(() => setDashboardMessage(''), 3000);
    }
  };

  // Create user profile if it doesn't exist
  const ensureUserProfile = async () => {
    if (!user) return;
    
    try {
      // Check if profile exists
      const { data: existingProfile, error: checkError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (checkError && checkError.code === 'PGRST116') {
        // Profile doesn't exist, create it
        console.log('Creating user profile...');
        const { error: insertError } = await supabase
          .from('profiles')
          .insert({
            id: user.id,
            email: user.email,
            subscription: 'free',
          });
        
        if (insertError) {
          console.error('Error creating profile:', insertError);
        } else {
          console.log('Profile created successfully');
          refreshUser();
        }
      } else if (existingProfile) {
        console.log('Profile exists:', existingProfile);
      }
    } catch (error) {
      console.error('Error ensuring user profile:', error);
    }
  };

  // Check authentication and handle success/cancel from Stripe
  useEffect(() => {
    if (!loading && user === null) {
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Handle Stripe success/cancel messages (only run once on mount)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const canceled = urlParams.get('canceled');
    
    if (success) {
      setDashboardMessage('Payment successful! Your subscription has been upgraded to Pro.');
      refreshUser();
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (canceled) {
      setDashboardMessage('Payment was canceled. You can try again anytime.');
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []); // Empty dependency array - only run once

  // Fetch saved GUIs for the logged-in user (only when user changes)
  useEffect(() => {
    async function fetchGUIs() {
      if (!user) return;
      setLoadingGUIs(true);
      console.log('Fetching saved GUIs for user:', user.id);
      const { data, error } = await supabase
        .from('saved_guis')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });
      if (error) {
        console.error('Error fetching saved GUIs:', error);
      } else {
        console.log('Fetched saved GUIs:', data);
        setSavedGUIs(data);
      }
      setLoadingGUIs(false);
    }
    fetchGUIs();
  }, [user?.id]); // Only depend on user ID, not the entire user object

  // Load export status when user changes
  useEffect(() => {
    const loadExportStatus = async () => {
      if (!user) {
        setExportStatus(null);
        return;
      }
      
      try {
        const status = await getMonthlyExportStatus(user);
        setExportStatus(status);
      } catch (error) {
        console.error('Error loading export status:', error);
        setExportStatus({
          can_export: false,
          exports_used: 0,
          exports_remaining: 0,
          reset_date: null,
          is_pro: false,
          error: 'Failed to load export status'
        });
      }
    };

    loadExportStatus();
  }, [user?.id]);

  // Manual refresh function for saved GUIs
  const refreshSavedGUIs = async () => {
    if (!user) return;
    setLoadingGUIs(true);
    console.log('Manually refreshing saved GUIs for user:', user.id);
    const { data, error } = await supabase
      .from('saved_guis')
      .select('*')
      .eq('user_id', user.id)
      .order('updated_at', { ascending: false });
    if (error) {
      console.error('Error refreshing saved GUIs:', error);
      setDashboardMessage('Error loading saved GUIs');
    } else {
      console.log('Refreshed saved GUIs:', data);
      setSavedGUIs(data);
      setDashboardMessage(`Loaded ${data.length} saved GUI(s)`);
      setTimeout(() => setDashboardMessage(''), 3000);
    }
    setLoadingGUIs(false);
  };

  // Get subscription display information
  const subscriptionDisplay = getSubscriptionDisplay(user);

  // Debug function to check subscription status
  const debugSubscriptionStatus = async () => {
    if (!user) return;
    
    try {
      // Get raw database data
      const { data: dbData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching database data:', error);
        return;
      }
      
      // Log both frontend and database data
      console.log('=== SUBSCRIPTION DEBUG ===');
      console.log('Frontend user data:', {
        subscription: user.subscription,
        subscription_status: user.subscription_status,
        subscription_end_date: user.subscription_end_date,
        stripe_customer_id: user.stripe_customer_id,
        subscription_id: user.subscription_id
      });
      console.log('Database data:', dbData);
      console.log('========================');
      
      // Show debug info in dashboard
      setDashboardMessage(`Debug: Frontend=${user.subscription}/${user.subscription_status}, DB=${dbData.subscription}/${dbData.subscription_status}`);
      setTimeout(() => setDashboardMessage(''), 5000);
      
    } catch (error) {
      console.error('Error in debug function:', error);
    }
  };

  // Show loading spinner while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex items-center justify-center">
        <SEO noindex={true} />
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-4">
            <svg className="w-8 h-8 text-white animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </div>
          <div className="text-gray-700 font-semibold">Loading your dashboard...</div>
          <div className="text-sm text-gray-500 mt-2">Preparing your PowerShell GUI Builder workspace</div>
        </div>
      </div>
    );
  }

  // Don't render the main content if user is not authenticated
  if (!user) {
    return null;
  }

  // Delete a saved GUI
  const handleDeleteGUI = async (gui) => {
    if (!window.confirm(`Delete GUI "${gui.name}"?`)) return;
    await supabase.from('saved_guis').delete().eq('id', gui.id);
    setSavedGUIs(savedGUIs.filter(g => g.id !== gui.id));
  };

  // (Optional) Open GUI in builder
  const handleOpenGUI = (gui) => {
    window.location.href = `/builder?open=${gui.id}`;
  };

  // Handle Stripe checkout
  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    try {
      // Use environment variable for Stripe price ID
      const priceId = process.env.REACT_APP_STRIPE_PRICE_ID || 'price_XXXXXXXXXXXXXX';
      await createCheckoutSession(priceId, user.id);
    } catch (err) {
      setDashboardMessage('Failed to start checkout. Please try again.');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Handle subscription cancellation
  const handleCancelSubscription = async () => {
    if (!window.confirm('Are you sure you want to cancel your Pro subscription? You will lose access to PowerShell export and save features.')) {
      return;
    }
    
    try {
      // First, get the latest user profile data to ensure we have the stripe_customer_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('stripe_customer_id, subscription_id')
        .eq('id', user.id)
        .single();
      
      if (profileError) {
        console.error('Error fetching profile:', profileError);
        setDashboardMessage('Failed to fetch subscription details. Please contact support.');
        return;
      }
      
      if (!profile.stripe_customer_id) {
        console.error('No stripe_customer_id found for user:', user.id);
        setDashboardMessage('No subscription found. Please contact support.');
        return;
      }
      
      console.log('Using stripe_customer_id:', profile.stripe_customer_id);
      
      // Redirect to Stripe customer portal for cancellation
      const response = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-portal-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          customerId: profile.stripe_customer_id,
          returnUrl: window.location.origin + '/dashboard'
        }),
      });
      
      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      } else {
        const errorData = await response.json();
        console.error('Portal session error:', errorData);
        setDashboardMessage('Failed to open cancellation portal. Please contact support.');
      }
    } catch (error) {
      console.error('Error opening cancellation portal:', error);
      setDashboardMessage('Failed to open cancellation portal. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <SEO noindex={true} />
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header Section */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome back, {user.email?.split('@')[0]}!</h1>
          <p className="text-gray-600">Manage your subscription and saved PowerShell GUIs</p>
        </div>

        {/* Dashboard Message */}
        {dashboardMessage && (
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-blue-700 font-medium">{dashboardMessage}</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Subscription Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center mr-3">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900">Subscription</h2>
                  <p className="text-sm text-gray-600">Manage your plan</p>
                </div>
              </div>

              <div className="space-y-4">
                {/* Current Plan Status */}
                <div className="p-4 bg-gray-50 rounded-xl">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-600">Current Plan</span>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${subscriptionDisplay.color}`}>
                      {subscriptionDisplay.status}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600">{subscriptionDisplay.description}</p>
                </div>

                {/* Warning for cancelled subscriptions */}
                {subscriptionDisplay.warning && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                    <div className="flex items-center mb-2">
                      <svg className="w-5 h-5 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-orange-800 font-semibold">Subscription Cancelled</span>
                    </div>
                    <p className="text-orange-700 text-sm">
                      You have {subscriptionDisplay.remainingDays} days of Pro access remaining.
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="space-y-3">
                  {user && !hasProAccess(user) && (
                    <>
                      {/* Monthly Export Status for Free Users */}
                      {exportStatus && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                          <div className="flex items-center mb-2">
                            <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-blue-800 font-semibold">Monthly Export Status</span>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <span className="text-sm text-blue-700">Exports used this month:</span>
                              <span className={`text-sm font-semibold ${exportStatus.can_export ? 'text-green-600' : 'text-red-600'}`}>
                                {exportStatus.exports_used}/1
                              </span>
                            </div>
                            {exportStatus.reset_date && (
                              <div className="text-xs text-blue-600">
                                Resets on {new Date(exportStatus.reset_date).toLocaleDateString()}
                              </div>
                            )}
                            {!exportStatus.can_export && (
                              <div className="text-xs text-red-600 font-medium">
                                Monthly limit reached. Upgrade to Pro for unlimited exports.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <button
                        className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        onClick={handleUpgrade}
                        disabled={checkoutLoading}
                      >
                        <div className="flex items-center justify-center">
                          {checkoutLoading ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Redirecting...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              Upgrade to Pro
                            </>
                          )}
                        </div>
                      </button>
                    </>
                  )}
                  
                  {user && hasProAccess(user) && (
                    <div className="space-y-3">
                      <div className="p-3 bg-green-50 border border-green-200 rounded-xl">
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="text-green-700 font-semibold">Pro Access Active</span>
                        </div>
                      </div>
                      
                      {/* Show different buttons based on subscription status */}
                      {user.subscription_status === 'cancelled_retain_access' ? (
                        // Resubscribe button for cancelled subscriptions
                        <button
                          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                          onClick={handleUpgrade}
                          disabled={checkoutLoading}
                        >
                          <div className="flex items-center justify-center">
                            {checkoutLoading ? (
                              <>
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Redirecting...
                              </>
                            ) : (
                              <>
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Resubscribe to Pro
                              </>
                            )}
                          </div>
                        </button>
                      ) : (
                        // Cancel button for active subscriptions
                        <button
                          className="w-full bg-red-600 text-white py-3 px-4 rounded-xl font-semibold hover:bg-red-700 transition-all duration-200 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                          onClick={handleCancelSubscription}
                        >
                          <div className="flex items-center justify-center">
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Cancel Subscription
                          </div>
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Manual Refresh Button */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => {
                      console.log('Current user data:', user);
                      refreshUser();
                      setTimeout(() => {
                        console.log('Refreshed user data:', user);
                      }, 1000);
                    }}
                    className="w-full px-3 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors duration-200"
                  >
                    Refresh Status
                  </button>
                  
                  {/* Debug button - remove in production */}
                  <button
                    onClick={async () => {
                      console.log('=== DEBUG USER DATA ===');
                      console.log('User object:', user);
                      console.log('User stripe_customer_id:', user?.stripe_customer_id);
                      console.log('User subscription:', user?.subscription);
                      console.log('User subscription_status:', user?.subscription_status);
                      
                      // Fetch fresh profile data
                      const { data: profile, error } = await supabase
                        .from('profiles')
                        .select('*')
                        .eq('id', user.id)
                        .single();
                      
                      console.log('Fresh profile data:', profile);
                      console.log('Profile error:', error);
                      console.log('=== END DEBUG ===');
                    }}
                    className="w-full px-3 py-2 bg-yellow-100 text-yellow-700 rounded-lg text-sm hover:bg-yellow-200 transition-colors duration-200 mt-2"
                  >
                    Debug User Data
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Saved GUIs Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-600 rounded-lg flex items-center justify-center mr-3">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Saved GUIs</h2>
                    <p className="text-sm text-gray-600">Your PowerShell GUI projects</p>
                  </div>
                </div>
                <button
                  onClick={refreshSavedGUIs}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-sm font-semibold hover:bg-indigo-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  disabled={loadingGUIs}
                >
                  <div className="flex items-center">
                    {loadingGUIs ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Refresh
                      </>
                    )}
                  </div>
                </button>
              </div>

              {loadingGUIs ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto mb-2"></div>
                    <div className="text-gray-600">Loading your GUIs...</div>
                  </div>
                </div>
              ) : savedGUIs.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No GUIs saved yet</h3>
                  <p className="text-gray-600 mb-6">Start building your first PowerShell GUI to see it here</p>
                  <button
                    onClick={() => navigate('/builder')}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
                  >
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create Your First GUI
                    </div>
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {savedGUIs.map(gui => (
                    <div key={gui.id} className="p-4 border border-gray-200 rounded-xl hover:border-indigo-300 hover:shadow-md transition-all duration-200">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 mb-1">{gui.name}</h3>
                          <p className="text-sm text-gray-600">
                            Last updated: {new Date(gui.updated_at).toLocaleDateString()} at {new Date(gui.updated_at).toLocaleTimeString()}
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleOpenGUI(gui)}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200"
                          >
                            <div className="flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                              Open
                            </div>
                          </button>
                          <button
                            onClick={() => handleDeleteGUI(gui)}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors duration-200"
                          >
                            <div className="flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 